// Overlays - DataOverlay, HoverCard, and FullDetailOverlay components
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { DataRow, ThemeType } from '../../types/visualization';
import { THEMES } from '../../constants/visualizationThemes';

// Interface for pinned search terms with colors
interface PinnedSearch {
  term: string;
  color: string;
}

// Helper function to highlight search terms in text
function highlightText(
  text: string,
  searchTerms: string[],
  pinnedSearches: PinnedSearch[],
  defaultHighlightColor: string = '#fbbf24'
): React.ReactNode {
  if (!text || (searchTerms.length === 0 && pinnedSearches.length === 0)) {
    return text;
  }

  // Build a map of terms to colors
  const termColorMap = new Map<string, string>();

  // Add pinned searches with their colors
  pinnedSearches.forEach(p => {
    if (p.term.trim()) {
      termColorMap.set(p.term.toLowerCase(), p.color);
    }
  });

  // Add regular search terms with default color
  searchTerms.forEach(term => {
    if (term.trim() && !termColorMap.has(term.toLowerCase())) {
      termColorMap.set(term.toLowerCase(), defaultHighlightColor);
    }
  });

  if (termColorMap.size === 0) return text;

  // Create regex pattern for all terms
  const allTerms = Array.from(termColorMap.keys()).map(t =>
    t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  );
  const regex = new RegExp(`(${allTerms.join('|')})`, 'gi');

  const parts = text.split(regex);

  return parts.map((part, i) => {
    const lowerPart = part.toLowerCase();
    const color = termColorMap.get(lowerPart);
    if (color) {
      return (
        <span
          key={i}
          style={{
            backgroundColor: color,
            color: '#000',
            padding: '0 2px',
            borderRadius: '2px',
            fontWeight: 600,
          }}
        >
          {part}
        </span>
      );
    }
    return part;
  });
}

// Data overlay with minimize support
interface DataOverlayProps {
  data: DataRow | null;
  onClose: () => void;
  theme: ThemeType;
  isMinimized: boolean;
  onMinimize: (minimized: boolean) => void;
  searchTerms?: string[];
  pinnedSearches?: PinnedSearch[];
}

export function DataOverlay({ data, onClose, theme, isMinimized, onMinimize, searchTerms = [], pinnedSearches = [] }: DataOverlayProps) {
  const [dragPos, setDragPos] = useState({ x: 20, y: 80 });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).tagName === 'BUTTON') return;
    setIsDragging(true);
    dragOffset.current = {
      x: e.clientX - dragPos.x,
      y: e.clientY - dragPos.y,
    };
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      setDragPos({
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y,
      });
    };

    const handleMouseUp = () => setIsDragging(false);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  if (!data) return null;
  const t = THEMES[theme];

  // Generic field ordering - show first few fields with values
  const orderedFields = Object.entries(data)
    .filter(([key, value]) => value !== undefined && value !== null && value !== '' && key !== '_id')
    .slice(0, 20);

  if (isMinimized) {
    const firstField = orderedFields[0];
    const identifier = firstField ? String(firstField[1]).slice(0, 30) : 'Row Data';

    return (
      <div
        onMouseDown={handleMouseDown}
        style={{
          position: 'absolute',
          top: dragPos.y,
          left: dragPos.x,
          background: theme === 'dark'
            ? 'linear-gradient(135deg, rgba(30, 35, 45, 0.98) 0%, rgba(20, 25, 35, 0.98) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(245, 247, 250, 0.98) 100%)',
          border: `1px solid ${theme === 'dark' ? 'rgba(80, 90, 110, 0.5)' : 'rgba(180, 190, 200, 0.6)'}`,
          borderRadius: '8px',
          padding: '12px 16px',
          color: t.text,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          minWidth: '200px',
          maxWidth: '260px',
          backdropFilter: 'blur(20px)',
          boxShadow: theme === 'dark'
            ? '0 4px 20px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05) inset'
            : '0 4px 20px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.5) inset',
          zIndex: 1000,
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: t.accent }}>{identifier}</div>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={() => onMinimize(false)}
              title="Expand"
              style={{
                background: t.buttonBg,
                border: `1px solid ${t.buttonBorder}`,
                color: t.textMuted,
                width: '20px',
                height: '20px',
                borderRadius: '3px',
                cursor: 'pointer',
                fontSize: '9px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ▢
            </button>
            <button
              onClick={onClose}
              title="Close"
              style={{
                background: t.buttonBg,
                border: `1px solid ${t.buttonBorder}`,
                color: t.textMuted,
                width: '20px',
                height: '20px',
                borderRadius: '3px',
                cursor: 'pointer',
                fontSize: '11px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ×
            </button>
          </div>
        </div>
        <div style={{ fontSize: '10px', color: t.textMuted, lineHeight: '1.6' }}>
          {orderedFields.slice(0, 4).map(([key, value]) => (
            <div key={key}>{key}: {highlightText(String(value).substring(0, 25), searchTerms, pinnedSearches)}</div>
          ))}
        </div>
        <div style={{ marginTop: '6px', fontSize: '8px', color: t.textDimmed, opacity: 0.6 }}>
          Drag to move • Click cube to update
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      background: t.panelBg,
      border: `1px solid ${t.panelBorder}`,
      borderRadius: '8px',
      padding: '24px',
      color: t.text,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      minWidth: '400px',
      maxWidth: '550px',
      maxHeight: '85vh',
      overflowY: 'auto',
      backdropFilter: 'blur(20px)',
      boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
      zIndex: 1000,
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        paddingBottom: '16px',
        borderBottom: `1px solid ${t.panelBorder}`,
      }}>
        <div>
          <div style={{ fontSize: '10px', color: t.textMuted, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '4px' }}>
            Row Details
          </div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: t.accent }}>
            {orderedFields[0] ? String(orderedFields[0][1]).slice(0, 40) : 'Data Row'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            onClick={() => onMinimize(true)}
            title="Minimize"
            style={{
              background: t.buttonBg,
              border: `1px solid ${t.buttonBorder}`,
              color: t.textMuted,
              width: '32px',
              height: '32px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ─
          </button>
          <button
            onClick={onClose}
            title="Close"
            style={{
              background: t.buttonBg,
              border: `1px solid ${t.buttonBorder}`,
              color: t.textMuted,
              width: '32px',
              height: '32px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ×
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {orderedFields.map(([key, value]) => (
          <div key={key} style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            padding: '6px 10px',
            background: t.inputBg,
            borderRadius: '4px',
            gap: '12px',
          }}>
            <span style={{
              color: t.textMuted,
              fontSize: '9px',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              flexShrink: 0,
              minWidth: '100px',
              paddingTop: '2px',
            }}>
              {key}
            </span>
            <span style={{
              color: t.text,
              fontSize: '12px',
              fontFamily: 'SF Mono, Menlo, monospace',
              textAlign: 'right',
              wordBreak: 'break-word',
            }}>
              {highlightText(String(value).substring(0, 100), searchTerms, pinnedSearches)}
            </span>
          </div>
        ))}
      </div>

      <div style={{
        marginTop: '16px',
        paddingTop: '12px',
        borderTop: `1px solid ${t.panelBorder}`,
        fontSize: '10px',
        color: t.textDimmed,
        textAlign: 'center',
      }}>
        Press ESC or click anywhere to close • Click ─ to minimize
      </div>
    </div>
  );
}

// Minimal hover card
interface HoverCardProps {
  data: DataRow | null;
  mousePos: { x: number; y: number } | null;
  theme: ThemeType;
  isVisible: boolean;
  searchTerms?: string[];
  pinnedSearches?: PinnedSearch[];
}

export function HoverCard({ data, mousePos, theme, isVisible, searchTerms = [], pinnedSearches = [] }: HoverCardProps) {
  if (!mousePos) return null;
  const t = THEMES[theme];

  const displayFields = data ? Object.entries(data)
    .filter(([key, value]) => value !== undefined && value !== null && value !== '' && key !== '_id')
    .slice(0, 4) : [];

  return (
    <div style={{
      position: 'fixed',
      left: mousePos.x + 15,
      top: mousePos.y + 15,
      background: t.panelBg,
      border: `1px solid ${t.panelBorder}`,
      borderRadius: '6px',
      padding: '10px 14px',
      color: t.text,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: '11px',
      backdropFilter: 'blur(10px)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
      zIndex: 999,
      pointerEvents: 'none',
      maxWidth: '280px',
      opacity: isVisible && data ? 1 : 0,
      transition: 'opacity 0.3s ease-in-out',
    }}>
      <div style={{ fontWeight: 600, fontSize: '12px', marginBottom: '6px', color: t.accent }}>
        {displayFields[0] ? highlightText(String(displayFields[0][1]).slice(0, 30), searchTerms, pinnedSearches) : 'Unknown'}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        {displayFields.slice(1).map(([key, value]) => (
          <div key={key}>
            <span style={{ color: t.textMuted }}>{key}:</span> {highlightText(String(value).substring(0, 30), searchTerms, pinnedSearches)}
          </div>
        ))}
      </div>
      <div style={{ marginTop: '6px', fontSize: '9px', color: t.textDimmed }}>
        Shift+click to zoom & view details
      </div>
    </div>
  );
}

// Full detail overlay for shift+click zoom
interface FullDetailOverlayProps {
  data: DataRow | null;
  onClose: () => void;
  theme: ThemeType;
  searchTerms?: string[];
  pinnedSearches?: PinnedSearch[];
}

export function FullDetailOverlay({ data, onClose, theme, searchTerms = [], pinnedSearches = [] }: FullDetailOverlayProps) {
  if (!data) return null;
  const t = THEMES[theme];

  const allFields = Object.entries(data)
    .filter(([key, value]) => key !== '_id' && value !== undefined && value !== null && value !== '');

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        backdropFilter: 'blur(8px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: t.panelBg,
          border: `1px solid ${t.panelBorder}`,
          borderRadius: '12px',
          padding: '24px',
          color: t.text,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          width: '90vw',
          maxWidth: '900px',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 25px 80px rgba(0,0,0,0.5)',
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: `1px solid ${t.panelBorder}`,
        }}>
          <div>
            <div style={{ fontSize: '10px', color: t.textMuted, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '4px' }}>
              Full Record Details
            </div>
            <div style={{ fontSize: '20px', fontWeight: 600, color: t.accent }}>
              {allFields[0] ? String(allFields[0][1]).slice(0, 50) : 'Data Row'}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: t.buttonBg,
              border: `1px solid ${t.buttonBorder}`,
              color: t.textMuted,
              width: '36px',
              height: '36px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ×
          </button>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '8px',
        }}>
          {allFields.map(([key, value]) => (
            <div key={key} style={{
              display: 'flex',
              flexDirection: 'column',
              padding: '8px 12px',
              background: t.inputBg,
              borderRadius: '4px',
              overflow: 'hidden',
            }}>
              <span style={{
                color: t.textMuted,
                fontSize: '9px',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                marginBottom: '2px',
              }}>
                {key}
              </span>
              <span style={{
                color: t.text,
                fontSize: '12px',
                fontFamily: 'SF Mono, Menlo, monospace',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
              }}>
                {highlightText(String(value).length > 200 ? String(value).substring(0, 200) + '...' : String(value), searchTerms, pinnedSearches)}
              </span>
            </div>
          ))}
        </div>

        <div style={{
          marginTop: '20px',
          paddingTop: '12px',
          borderTop: `1px solid ${t.panelBorder}`,
          fontSize: '10px',
          color: t.textDimmed,
          textAlign: 'center',
        }}>
          Press ESC or click outside to close • {allFields.length} fields shown
        </div>
      </div>
    </div>
  );
}
